import { formatLocale, parseLocale } from './locales';
import { MARKETS, findMarket } from './markets';

/**
 * The languages plant.habb.me speaks, as DATA — the axis a visitor actually
 * chooses along.
 *
 * A URL carries a MARKET and a LANGUAGE (CON-002), but someone hunting for their
 * own tongue is not choosing a country: offering all four market-language pairs
 * made them read a matrix to answer a one-word question, and two of the four rows
 * said "English" twice. So the switcher offers three LANGUAGES and infers the
 * market — see `resolveLocaleForLanguage`.
 *
 * `endonym` is the language's name in itself and is deliberately NOT a message
 * key: a visitor who cannot read the language currently on screen still has to be
 * able to find theirs, so these three strings never translate.
 *
 * `flagCode` is the nation whose flag stands for the language HERE. German is
 * Austria's flag, not Germany's, because Austria is the market we serve German
 * to — the plants, the hardiness zone and the planting calendar are Austrian.
 * English has no market of its own and takes the flag of the language's origin.
 *
 * The set is held to the market registry by `languages.test.js`: a language a
 * market serves and this file omits is a locale nobody can reach.
 */
export const LANGUAGES = [
    { code: 'de', endonym: 'Deutsch', flagCode: 'at' },
    { code: 'en', endonym: 'English', flagCode: 'gb' },
    { code: 'hu', endonym: 'Magyar', flagCode: 'hu' },
];

export function findLanguage(languageCode) {
    return LANGUAGES.find((language) => language.code === languageCode) || null;
}

/** The market a language belongs to when the visitor's own cannot serve it. */
function findHomeMarket(languageCode) {
    return MARKETS.find((market) => market.languages.includes(languageCode)) || null;
}

/**
 * The locale a visitor reaches by choosing a language from where they stand.
 *
 * Holding the market still is the point: a gardener reading Austrian content in
 * German who switches to English wants the same garden in English, not a different
 * country's. The market moves only when it has to — Austria has no Hungarian and
 * Hungary has no German — which is the one case where the language IS the country.
 */
export function resolveLocaleForLanguage(currentLocaleCode, languageCode) {
    const currentMarket = findMarket(parseLocale(currentLocaleCode)?.marketCode);
    const market = currentMarket?.languages.includes(languageCode) ? currentMarket : findHomeMarket(languageCode);

    return market ? formatLocale(market.code, languageCode) : null;
}

/** Every language the switcher offers, each already resolved to the locale it leads to. */
export function listLanguageChoices(currentLocaleCode) {
    return LANGUAGES.map((language) => ({
        ...language,
        localeCode: resolveLocaleForLanguage(currentLocaleCode, language.code),
    }));
}
